import React, { useState, useRef } from 'react';
import './ChangePassword.css';
import '../LoginPage/LoginPage.css';
import { useDispatch, useSelector } from 'react-redux';
import {
    passwordChanged,
    signOut,
    selectUserName
} from '../../store/pageLayoutSlice';
import { apiCall } from '../../APICalls/APICalls';

/**
 * Shown when the account carries mustChangePassword - a newly provisioned
 * account, or one an administrator has reset.
 *
 * The server refuses every other endpoint for such an account, so this is not
 * a suggestion that can be dismissed: it is the only thing the user can do
 * besides sign out. The rules echoed below are the server's, which is what
 * actually enforces them; they are repeated here only so the user is not
 * guessing.
 */
function ChangePassword() {

    const dispatch = useDispatch();
    const userName = useSelector( selectUserName );

    const currentRef = useRef(null);
    const newRef = useRef(null);
    const confirmRef = useRef(null);

    const [ errors, setErrors ] = useState([]);
    const [ isSubmitting, setIsSubmitting ] = useState(false);

    function describeError(error) {
        if (!error.response) {
            return ['Could not reach the server. Check your connection and try again.'];
        }
        const status = error.response.status;
        const data = error.response.data || {};

        if (status === 400 && data.error === 'weak_password') {
            return data.messages || ['That password does not meet the requirements.'];
        }
        if (status === 401) {
            return ['Your current password is not correct.'];
        }
        if (status === 429) {
            return ['Too many attempts. Please wait a few minutes and try again.'];
        }
        return ['Could not change the password. Please try again.'];
    }

    function handleSubmit() {
        if ( isSubmitting ) return;

        const currentPassword = currentRef.current ? currentRef.current.value : '';
        const newPassword = newRef.current ? newRef.current.value : '';
        const confirmPassword = confirmRef.current ? confirmRef.current.value : '';

        if ( !currentPassword || !newPassword ) {
            setErrors(['Fill in every field.']);
            return;
        }
        // Checked here rather than on the server because it is a typo check,
        // not a security rule - the server never sees the confirmation.
        if ( newPassword !== confirmPassword ) {
            setErrors(['The new passwords do not match.']);
            return;
        }

        setErrors([]);
        setIsSubmitting(true);

        apiCall('auth/changePassword', {
            currentPassword: currentPassword,
            newPassword: newPassword
        }).then( res => {
            if ( res.data && res.data.success === true ) {
                // The server has replaced every session for this account,
                // including this one, and issued a fresh cookie.
                dispatch( passwordChanged() );
            } else {
                setErrors(['Could not change the password. Please try again.']);
            }
            setIsSubmitting(false);
        }).catch( error => {
            setErrors( describeError( error ) );
            setIsSubmitting(false);
        });
    }

    function handleKeyDown(e) {
        if ( e.keyCode === 13 ) handleSubmit();
    }

    return (
        <div id="login_page">
            <div id="login_container">
                <h1 className="login_title">Choose a Password</h1>

                <p className="change_password_intro">
                    { userName ? userName + ', this' : 'This' } account is using a
                    temporary password. Choose a new one to continue.
                </p>

                {
                    errors.length > 0 &&
                    <ul className="change_password_errors">
                        { errors.map( (message, i) => (
                            <li key={ i } className="login_error">{ message }</li>
                        )) }
                    </ul>
                }

                <div className="loginInputContainer">
                    <label className="loginInputLabel" htmlFor="currentPassword">
                        Temporary password
                    </label>
                    <input
                        type="password"
                        id="currentPassword"
                        className="loginInput"
                        autoComplete="current-password"
                        ref={ currentRef }
                        onKeyDown={ handleKeyDown }
                    />
                </div>

                <div className="loginInputContainer">
                    <label className="loginInputLabel" htmlFor="newPassword">
                        New password
                    </label>
                    <input
                        type="password"
                        id="newPassword"
                        className="loginInput"
                        autoComplete="new-password"
                        ref={ newRef }
                        onKeyDown={ handleKeyDown }
                    />
                    <p className="change_password_hint">
                        At least 12 characters. A memorable phrase of a few words is
                        both stronger and easier to type than a short password with
                        substituted symbols. It cannot contain your name or email
                        address.
                    </p>
                </div>

                <div className="loginInputContainer">
                    <label className="loginInputLabel" htmlFor="confirmPassword">
                        Confirm new password
                    </label>
                    <input
                        type="password"
                        id="confirmPassword"
                        className="loginInput"
                        autoComplete="new-password"
                        ref={ confirmRef }
                        onKeyDown={ handleKeyDown }
                    />
                </div>

                <div className="login_button_container">
                    <button
                        id="SSO_button"
                        className="BaseButton"
                        onClick={ handleSubmit }
                        disabled={ isSubmitting }
                    >
                        { isSubmitting ? 'Saving...' : 'Set Password' }
                    </button>
                    <button
                        className="change_password_signout"
                        onClick={ () => dispatch( signOut() ) }
                    >
                        Sign out
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ChangePassword;
